import { Grid, Button, Divider, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../stores/store';
import {
  updateJudgement,
  updateMemo,
  updateNumericalValue,
} from '../../../stores/inspectionTypeData/slice';
import { isEmpty } from '../../../_utils/inspectionUtils';
import { useTranslation } from 'react-i18next';
import {
  INPUT_TYPE,
  JUDGEMENT,
  VARIABLE_HARDNESS,
} from '../../../_constants/inspectionConst';
import { ArrowBackIosSharp, ArrowForwardIosSharp } from '@mui/icons-material';
import { useMemo } from 'react';
import { GetInspectionItem } from '../../../services/apiTypes';
import { INSPECTION_DISPLAY_TYPE } from '../../../_constants/manageConst';
import { isInspection } from '../../../_utils/utils';

type InspectionHeaderProps = {
  itemDataList: GetInspectionItem[];
  displayType: number;
  changeType: (newCategoryId: string, newTypeId: string) => void;
  isInputAutoLeakTest(itemData: GetInspectionItem[]): boolean;
};

/** 前へ・次へボタン 全てOK・N/A・クリアボタン */
const InspectionHeader: React.FC<InspectionHeaderProps> = ({
  itemDataList,
  displayType,
  changeType,
  isInputAutoLeakTest,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  const { inspectionCategoryList } = useSelector(
    (state: RootState) => state.inspectionTop.inspectionTop,
  );
  const { typeLabelsMap } = useSelector((state: RootState) => state.typeLabels);

  const typeList = useMemo(
    () => typeLabelsMap[categoryId] || [],
    [categoryId, typeLabelsMap],
  );
  /** 全て数値入力か */
  const isAllNumberInput = useMemo(
    () =>
      typeId === VARIABLE_HARDNESS.typeId ||
      itemDataList.every((item) => item.inputType === 1),
    [typeId, itemDataList],
  );

  const isFirstType = typeList[0] && typeList[0].id === typeId;
  const isLastType =
    typeList[typeList.length - 1] &&
    typeList[typeList.length - 1].id === typeId;

  const style = {
    /** 全てOK・N/A・クリアボタン */
    button: {
      height: '2rem',
      padding: '3px 5px',
      fontSize: theme.typography.caption.fontSize,
      boxShadow:
        '0px 1px 1px -2px rgba(0, 0, 0, 0.2), 0px 1px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
    },
    /** 前へ・次へボタン */
    arrowButton: {
      fontSize: theme.typography.caption.fontSize,
      pb: 0,
      color: '#000000',
      background: 'none',
    },
  };

  /** 全てOKボタン押下時 */
  const allOk = () => {
    itemDataList.forEach((depth0) => {
      // 数値入力には適用しない
      if (depth0.inputType === INPUT_TYPE.NUMBER) {
        return;
      }

      // マトリクス形式は階層0を直接更新しない
      // 空のものだけOKにする
      if (
        displayType !== INSPECTION_DISPLAY_TYPE.MATRIX.value &&
        isEmpty(depth0.judgement)
      ) {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [depth0.id],
            judgement: JUDGEMENT.OK,
          }),
        );
      }

      depth0.itemData.forEach((depth1) => {
        // 空のものだけOKにする
        if (isEmpty(depth1.judgement)) {
          dispatch(
            updateJudgement({
              categoryId,
              typeId,
              path: [depth0.id, depth1.id],
              judgement: JUDGEMENT.OK,
            }),
          );
          depth1.itemData.forEach((depth2) => {
            dispatch(
              updateJudgement({
                categoryId,
                typeId,
                path: [depth0.id, depth1.id, depth2.id],
                judgement: JUDGEMENT.OK,
              }),
            );
          });
        }
      });
    });
  };

  /** 全てN/Aボタン押下時 */
  const allNa = () => {
    itemDataList.forEach((depth0) => {
      // マトリクス形式は階層0を直接更新しない
      // 空のものだけN/Aにする
      if (
        displayType !== INSPECTION_DISPLAY_TYPE.MATRIX.value &&
        isEmpty(depth0.judgement)
      ) {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [depth0.id],
            judgement: JUDGEMENT.NA,
          }),
        );
      }

      depth0.itemData.forEach((depth1) => {
        // 空のものだけN/Aにする
        if (isEmpty(depth1.judgement)) {
          dispatch(
            updateJudgement({
              categoryId,
              typeId,
              path: [depth0.id, depth1.id],
              judgement: JUDGEMENT.NA,
            }),
          );
          depth1.itemData.forEach((depth2) => {
            dispatch(
              updateJudgement({
                categoryId,
                typeId,
                path: [depth0.id, depth1.id, depth2.id],
                judgement: JUDGEMENT.NA,
              }),
            );
          });
        }
      });
    });
  };

  /** クリアボタン押下時 */
  const allClear = () => {
    itemDataList.forEach((depth0) => {
      // 階層0をクリア
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [depth0.id],
          judgement: JUDGEMENT.EMPTY,
        }),
      );
      dispatch(
        updateNumericalValue({
          categoryId,
          typeId,
          path: [depth0.id],
          numericalValue: null, // 未入力にする
        }),
      );
      dispatch(
        updateMemo({
          categoryId,
          typeId,
          path: [depth0.id],
          memo: '', // 未入力にする
        }),
      );

      // 階層1をクリア
      depth0.itemData.forEach((depth1) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [depth0.id, depth1.id],
            judgement: JUDGEMENT.EMPTY,
          }),
        );
        dispatch(
          updateNumericalValue({
            categoryId,
            typeId,
            path: [depth0.id, depth1.id],
            numericalValue: null, // 未入力にする
          }),
        );
        dispatch(
          updateMemo({
            categoryId,
            typeId,
            path: [depth0.id, depth1.id],
            memo: '', // 未入力にする
          }),
        );

        // 階層2をクリア
        depth1.itemData.forEach((depth2) => {
          dispatch(
            updateJudgement({
              categoryId,
              typeId,
              path: [depth0.id, depth1.id, depth2.id],
              judgement: JUDGEMENT.EMPTY,
            }),
          );
          dispatch(
            updateNumericalValue({
              categoryId,
              typeId,
              path: [depth0.id, depth1.id, depth2.id],
              numericalValue: null, // 未入力にする
            }),
          );
          dispatch(
            updateMemo({
              categoryId,
              typeId,
              path: [depth0.id, depth1.id, depth2.id],
              memo: '', // 未入力にする
            }),
          );
        });
      });
    });
  };

  /** 前へボタン押下時 */
  const onClickPrevious = () => {
    if (isFirstType) {
      // 先頭の場合、前の大項目に遷移
      const currentIndex = inspectionCategoryList.findIndex(
        (category) => category.id === categoryId,
      );
      const prevIndex =
        currentIndex > 0 ? currentIndex - 1 : inspectionCategoryList.length - 1;
      const prevCategoryId = inspectionCategoryList[prevIndex].id;
      const newTypeId =
        typeLabelsMap[prevCategoryId][typeLabelsMap[prevCategoryId].length - 1]
          .id;
      changeType(prevCategoryId, newTypeId);
      return;
    }
    // 前の中項目に遷移
    const currentIndex = typeList.findIndex((type) => type.id === typeId);
    if (currentIndex === -1) {
      return;
    }
    changeType(categoryId, typeList[currentIndex - 1].id);
  };

  /** 次へボタン押下時 */
  const onClickNext = () => {
    // 気密検査で次へボタン押下時に浸水確認をスキップするか
    const isSkip =
      repairRequest &&
      isInspection(repairRequest.status) &&
      isInputAutoLeakTest(itemDataList);

    if (isLastType || isSkip) {
      // 末端の場合、次の大項目に遷移
      const currentIndex = inspectionCategoryList.findIndex(
        (category) => category.id === categoryId,
      );
      const nextIndex =
        currentIndex < inspectionCategoryList.length - 1 ? currentIndex + 1 : 0;
      const nextCategoryId = inspectionCategoryList[nextIndex].id;
      const newTypeId = typeLabelsMap[nextCategoryId][0].id;
      changeType(nextCategoryId, newTypeId);
      return;
    }
    // 次の中項目に遷移
    const currentIndex = typeList.findIndex((type) => type.id === typeId);
    if (currentIndex === -1) {
      return;
    }
    changeType(categoryId, typeList[currentIndex + 1].id);
  };

  return (
    <>
      <Grid container spacing={1} sx={{ m: 0 }}>
        <Grid size={{ xs: 2.25 }} container justifyContent="flex-start">
          <Button
            variant="text"
            onClick={onClickPrevious}
            sx={style.arrowButton}
          >
            <ArrowBackIosSharp
              color="primary"
              fontSize="small"
              sx={{
                mr: 1,
              }}
            />
            {t('BTN_PREV')}
          </Button>
        </Grid>
        <Grid size={{ xs: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            sx={style.button}
            onClick={allOk}
            disabled={isAllNumberInput}
          >
            {t('BTN_ALL_OK')}
          </Button>
        </Grid>
        <Grid size={{ xs: 2.5 }}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              ...style.button,
              backgroundColor: '#5C5C5C',
            }}
            onClick={allNa}
          >
            {t('BTN_ALL_NA')}
          </Button>
        </Grid>
        <Grid size={{ xs: 2.5 }}>
          <Button
            variant="outlined"
            fullWidth
            sx={style.button}
            onClick={allClear}
          >
            {t('BTN_CLEAR')}
          </Button>
        </Grid>
        <Grid size={2.25} container justifyContent="flex-end">
          <Button variant="text" onClick={onClickNext} sx={style.arrowButton}>
            {t('BTN_NEXT')}
            <ArrowForwardIosSharp
              color="primary"
              fontSize="small"
              sx={{
                ml: 1,
              }}
            />
          </Button>
        </Grid>
      </Grid>

      <Divider
        variant="middle"
        sx={{
          width: '100%',
          margin: '0.5rem 0 1rem',
        }}
      />
    </>
  );
};

export default InspectionHeader;
